import { AnimatePresence, motion } from "motion/react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import {
  IllustrationAnalyticsTeam,
  IllustrationDataChart,
  IllustrationFinancialChart,
} from "../assets/Illustrations";
import { useAuth } from "../hooks";
import { UserPlus, Plug, ChartLine } from "@phosphor-icons/react";
import SignIn from "./SignIn";

const Auth: React.FC = () => {
  const { t } = useTranslation();
  const { isAuthenticated, permittedRoutes } = useAuth();
  const [currentFrame, setCurrentFrame] = useState(0);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentFrame((prev) => (prev + 1) % 4);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const mode = searchParams.get("mode");
    if (mode === "signup") {
      navigate("/auth", { replace: true });
    }
  }, [searchParams, navigate]);

  if (isAuthenticated && permittedRoutes.length > 0) {
    return <Navigate to={permittedRoutes[0]?.path || "/dashboard"} replace />;
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen flex flex-col"
    >
      <div className="flex-1 flex">
        {/* Left side illustration - Only visible on desktop */}
        <motion.div
          className={`hidden lg:flex lg:w-1/2 relative overflow-hidden bg-indigo-100 z-10 shadow-xl flex-col`}
          initial={{
            x: 0,
            borderRadius: "0 2rem 2rem 0",
          }}
          animate={{
            x: 0,
            borderRadius: "0 2rem 2rem 0",
          }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
        >
          {[
            {
              title: t("auth.features.trackPerformance.title"),
              description: t("auth.features.trackPerformance.description"),
              image: <IllustrationAnalyticsTeam scale={5} />,
            },
            {
              title: t("auth.features.optimizeAds.title"),
              description: t("auth.features.optimizeAds.description"),
              image: <IllustrationDataChart scale={5} />,
            },
            {
              title: t("auth.features.growRevenue.title"),
              description: t("auth.features.growRevenue.description"),
              image: <IllustrationFinancialChart scale={5} />,
            },
            {
              title: t("auth.features.howItWorks.title", "How it Works"),
              description: t(
                "auth.features.howItWorks.description",
                "Login → Integration → Admob-Ads Analytics"
              ),
              isHowItWorks: true,
            },
          ].map((frame, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 flex flex-col justify-center items-center text-white p-12 ${
                currentFrame === index ? "opacity-100" : "opacity-0"
              }`}
            >
              {frame.isHowItWorks ? (
                <div className="flex flex-col items-center justify-center h-full">
                  <h2 className="text-4xl font-bold mb-12 text-center text-gray-900 dark:text-white">
                    {frame.title}
                  </h2>
                  <div className="flex flex-col gap-8 items-center justify-center">
                    <div className="flex flex-col items-center">
                      <div className="w-12 h-12 rounded-full bg-green-400 flex items-center justify-center text-white font-bold text-lg mb-3">
                        <UserPlus size={24} weight="bold" />
                      </div>
                      <p className="text-center font-semibold text-gray-900 dark:text-white">
                        Login
                      </p>
                      <p className="text-sm text-center text-gray-700 dark:text-gray-300 mt-2 max-w-xs">
                        Set up an account and complete the email verification
                        process
                      </p>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="w-12 h-12 rounded-full bg-green-400 flex items-center justify-center text-white font-bold text-lg mb-3">
                        <Plug size={24} weight="bold" />
                      </div>
                      <p className="text-center font-semibold text-gray-900 dark:text-white">
                        Integration
                      </p>
                      <p className="text-sm text-center text-gray-700 dark:text-gray-300 mt-2 max-w-xs">
                        Connect your Google Ads and Google AdMob accounts
                      </p>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="w-12 h-12 rounded-full bg-green-400 flex items-center justify-center text-white font-bold text-lg mb-3">
                        <ChartLine size={24} weight="bold" />
                      </div>
                      <p className="text-center font-semibold text-gray-900 dark:text-white">
                        Analytics
                      </p>
                      <p className="text-sm text-center text-gray-700 dark:text-gray-300 mt-2 max-w-xs">
                        Analyze your data at the country level
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {frame.image}
                  <h2 className="text-4xl font-bold mt-8 mb-4 text-center text-gray-900 dark:text-white">
                    {frame.title}
                  </h2>
                  <p className="text-xl text-center max-w-md text-gray-700 dark:text-gray-300">
                    {frame.description}
                  </p>
                </>
              )}
            </div>
          ))}

          {/* Footer with links and copyright */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/15 to-transparent p-6">
            <div className="flex justify-center gap-4 mb-4 text-sm">
              <a
                href="https://apprevbooster.com/privacy-policy"
                target="_blank"
                className="text-primary hover:text-secondary-600 transition-colors underline"
              >
                {t("auth.footer.privacyPolicy", "Privacy Policy")}
              </a>
              <span className="text-primary">|</span>
              <a
                href="https://apprevbooster.com/terms-of-service"
                target="_blank"
                className="text-primary hover:text-secondary-600 transition-colors underline"
              >
                {t("auth.footer.termsAndConditions", "Terms of Service")}
              </a>
            </div>
            <p className="text-center text-xs text-primary">
              {t(
                "auth.footer.copyright",
                `© ${new Date().getFullYear()} AppRevBooster. All rights reserved.`
              )}
            </p>
          </div>
        </motion.div>

        {/* Right side - Login & Language Switcher */}
        <div className="w-full lg:w-1/2 flex flex-col items-center justify-center bg-gray-50 relative">
          {/* Desktop Animation Container */}
          <motion.div
            className="hidden lg:block w-full"
            initial={{ x: 0, opacity: 1 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
          >
            <AnimatePresence mode="wait">
              <motion.div
                initial={{ opacity: 0, width: "100%" }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <SignIn />
              </motion.div>
            </AnimatePresence>
          </motion.div>

          {/* Mobile Animation Container */}
          <div className="block lg:hidden w-full">
            <AnimatePresence mode="wait">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <SignIn />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default Auth;
